/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState, useRef } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { clsx } from 'clsx';
import { useRouter } from 'next/router';
import { getActiveGolfTab, trackImpression, useGetBreakpoint } from '../../modules/utils/helpers';
import { CreateNavBarOverlay, StyledNavigationTab, StyledNavigationTabMobile } from './NavigationTab.styles';
import { Button, Types } from '@marriott/mi-ui-library';
import { NavBarProps, NavigationTabProps } from './NavigationTab.types';
import { fullbleedClass, TRACKING_CONSTANT } from '../../modules/utils/constants/constants';
import { PageParamsContext } from '../../modules/context/PageContext';

const { NEXT_PUBLIC_AEM_SITE } = process.env;

const NavBarItems = (props: NavBarProps) => {
  const {
    tabList,
    activeTab,
    listStyleClass,
    listItemStyleClass,
    listItemSpanClass,
    currentPage,
    isPremium,
    isInverse,
  } = props;
  const { GOLF_SUBNAVIGATION_TAB, INTERNAL_LINK } = TRACKING_CONSTANT;
  const scrollRef = useRef<HTMLUListElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollLeft = el.scrollLeft;
    const scrollWidth = el.scrollWidth;
    const clientWidth = el.clientWidth;

    // Add a small buffer to account for rounding issues
    const buffer = 5;

    setShowLeftArrow(scrollLeft > buffer);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - buffer);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (el) {
      el.scrollLeft = 0;
    }
    if (el) el.addEventListener('scroll', updateArrows);
    return () => el?.removeEventListener('scroll', updateArrows);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = 150;
    el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
  };

  return isPremium ? (
    <div className="nav-scroll-wrapper d-flex align-items-center justify-content-center">
      {showLeftArrow && (
        <Button
          className={clsx('px-2 py-1 icon-l icon-arrow-left', isInverse ? 'icon-inverse-bg' : 'icon-standard-bg')}
          callback={() => scroll('left')}
        />
      )}
      <ul
        className={clsx(listStyleClass, 'nav-scroll-container')}
        role="tablist"
        ref={scrollRef}
        style={{ overflowX: 'auto', whiteSpace: 'nowrap', scrollBehavior: 'smooth', marginTop: 'auto' }}
      >
        {tabList?.map((tabItem, index) => (
          <li
            key={tabItem?.navbarLabel}
            className={clsx(
              listItemStyleClass,
              index === 0 && 'first-tab-no-margin',
              activeTab === tabItem?.navbarLabel && 'active'
            )}
          >
            <a
              className={clsx(listItemSpanClass, 'item-heading custom_click_track')}
              href={tabItem?.navbarUrl}
              target={tabItem?.openInNewTab ? '_blank' : '_self'}
              rel="noreferrer"
              data-custom_click_track_value={`${GOLF_SUBNAVIGATION_TAB} on ${currentPage}|${tabItem?.navbarLabel} TabItem|${INTERNAL_LINK}`}
            >
              {tabItem?.navbarLabel}
            </a>
          </li>
        ))}
      </ul>
      {showRightArrow && (
        <Button
          className={clsx('px-2 py-1 icon-l icon-arrow-right', isInverse ? 'icon-inverse-bg' : 'icon-standard-bg')}
          callback={() => scroll('right')}
        />
      )}
    </div>
  ) : (
    <ul className={listStyleClass} role="tablist">
      {tabList?.map((tabItem, index) => (
        <li
          role="tab"
          onClick={e => {
            e.currentTarget.focus();
          }}
          tabIndex={index}
          className={clsx(listItemStyleClass, activeTab === tabItem?.navbarLabel ? 'active' : 'inactive-tab')}
          aria-expanded={activeTab === tabItem?.navbarLabel ? 'true' : 'false'}
          data-testid={tabItem?.navbarLabel}
        >
          <a
            className={clsx(listItemSpanClass, 'item-heading custom_click_track')}
            href={tabItem?.navbarUrl}
            target={tabItem?.openInNewTab ? '_blank' : '_self'}
            rel="noreferrer"
            data-custom_click_track_value={`${GOLF_SUBNAVIGATION_TAB} on ${currentPage}|${tabItem?.navbarLabel} TabItem|${INTERNAL_LINK}`}
          >
            {tabItem?.navbarLabel}
          </a>
        </li>
      ))}
    </ul>
  );
};

export const NavigationTab: React.FC<NavigationTabProps> = props => {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState(getActiveGolfTab(router?.pathname, props?.['navbar-props']));
  const [showNavbar, setShowNavbar] = useState(false);
  let portSize = useGetBreakpoint();
  const [isMobileViewPort, setIsMobileViewPort] = useState(portSize === 'mobile');
  const { currentPage } = useContext(PageParamsContext);

  useEffect(() => {
    function handleResize() {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      portSize = useGetBreakpoint();
      setIsMobileViewPort(portSize === 'mobile');
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setActiveTab(getActiveGolfTab(router.pathname, props?.['navbar-props']));
  }, [router.pathname]);

  useEffect(() => {
    if (props?.trackingProperties?.impressionTrack)
      trackImpression(props?.trackingProperties || {}, 'Golf Navigation Tab' || '');
  }, [props?.trackingProperties]);

  const handleShowNavbar = () => {
    setShowNavbar(!showNavbar);
  };

  useEffect(() => {
    const handleHashChange = () => {
      if (props?.isPremium) {
        const hash = window.location.hash.replace('#', '');
        const matchedTab = props?.['navbar-props']?.find(tab => tab.navbarUrl === `#${hash}`);
        setActiveTab(matchedTab?.navbarLabel || '');
      }
    };

    if (props?.isPremium) {
      window.addEventListener('hashchange', handleHashChange);
      handleHashChange();
    }

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [props?.isPremium, props?.['navbar-props']]);

  return props?.['navbar-props'] && props?.['navbar-props']?.length > 0 ? (
    props?.isPremium ? (
      <StyledNavigationTab
        className={clsx('pt-3', props?.isPremium ? props?.premiumMenuClass : props?.styleclass || 'alternate')}
        isPremium={props?.isPremium || false}
      >
        <nav
          className={clsx('container', {
            'px-md-0': props?.styleclass?.includes(fullbleedClass),
            'p-0': !props?.styleclass?.includes(fullbleedClass),
          })}
        >
          <NavBarItems
            tabList={props?.['navbar-props']}
            activeTab={activeTab || ''}
            listStyleClass="m-standard-tab-list"
            listItemStyleClass={clsx(props?.isPremium ? 'mr-5' : 'pb-3 mr-4', 'm-standard-tab-list-item with-border')}
            listItemSpanClass={clsx(props?.isPremium ? 't-font-m' : 't-label-s')}
            currentPage={currentPage}
            isPremium={props?.isPremium}
            isInverse={props?.premiumMenuClass == 'alternate'}
          />
        </nav>
      </StyledNavigationTab>
    ) : isMobileViewPort ? (
      <StyledNavigationTabMobile showNavbar={showNavbar} className={props?.styleclass}>
        {showNavbar && <CreateNavBarOverlay />}
        <div className="mobile__title-container d-flex p-3 align-items-center">
          <span className="t-subtitle-l flex-grow-1 text-center">{activeTab}</span>

          <Button
            type={Types.ButtonTypeVariation.Button}
            className="p-0 mobile__hamburger-button"
            callback={handleShowNavbar}
            testId={showNavbar ? 'icon-clear' : 'icon-dropdown-down'}
          >
            <span className={clsx('p-0', showNavbar ? 'icon-clear' : 'icon-dropdown-down')}></span>
          </Button>
        </div>
        {showNavbar && (
          <div className="mobile__nav-bar-container p-4">
            <NavBarItems
              tabList={props?.['navbar-props']}
              activeTab=""
              listStyleClass="p-0 m-0 mobile__list-style"
              listItemStyleClass="mobile__list-item-style"
              listItemSpanClass="t-font-m"
              currentPage={currentPage}
            />
          </div>
        )}
      </StyledNavigationTabMobile>
    ) : (
      <StyledNavigationTab className={clsx('pt-3', props?.styleclass)} isPremium={props?.isPremium || false}>
        <nav
          className={clsx('container', {
            'px-md-0': props?.styleclass?.includes(fullbleedClass),
            'p-0': !props?.styleclass?.includes(fullbleedClass),
          })}
        >
          <NavBarItems
            tabList={props?.['navbar-props']}
            activeTab={activeTab || ''}
            listStyleClass="m-standard-tab-list"
            listItemStyleClass={clsx(props?.isPremium ? 'pb-2 mb-1 mr-5' : 'pb-3 mr-4', 'm-standard-tab-list-item')}
            listItemSpanClass="t-label-s"
            currentPage={currentPage}
          />
        </nav>
      </StyledNavigationTab>
    )
  ) : null;
};

export const NavigationTabConfig = {
  emptyLabel: 'navigationtab',
  isEmpty: false,
  resourceType: `${NEXT_PUBLIC_AEM_SITE}/components/content/golf/navigationtab`,
};

export const NavigationTabEditable = (props: any) => {
  return (
    <EditableComponent config={NavigationTabConfig} {...props}>
      <NavigationTab {...props} />
    </EditableComponent>
  );
};
